import type { AdminUserActivity, AdminUserSession } from '@/admin/adminApi';

/**
 * Mise en forme de l'usage d'un compte (sessions, activite de synchronisation),
 * ecrite **hors des composants** : elle se teste sans rendu, et ce que l'onglet
 * et la carte de synchronisation disent de la meme donnee ne peut pas diverger.
 *
 * Aucun nombre n'est calcule ici : ceux de l'ecran sont ceux du serveur. Ce
 * module ne fait que les nommer.
 */

/** Periode lue : fixee, comme la maquette (« Activite · 30 jours »). */
export const ACTIVITY_DAYS = 30;

/**
 * Natures d'enregistrement du journal, dites en francais.
 *
 * Les valeurs sont celles que le mobile emet (`grep -rn "entityType:" mobile/src`).
 * **Une nature inconnue s'affiche telle que le serveur l'a ecrite**, jamais
 * rangee dans « autre » : si le mobile en gagne une, l'ecran la montre.
 */
export const ENTITY_TYPE_LABELS: Readonly<Record<string, string>> = {
  feeding_session: 'Séances d’alimentation',
  pumping_session: 'Tirages',
  solid_meal: 'Repas solides',
  milk_batch: 'Lots de lait',
  stock_movement: 'Mouvements de stock',
  growth_measurement: 'Mesures de croissance',
  baby_profile: 'Profils bébé',
  diversification_food: 'Aliments du carnet',
  food_reaction: 'Réactions alimentaires',
  storage_location: 'Lieux de stockage',
  storage_sub_location: 'Sous-emplacements',
  security_consent: 'Consentements',
  baby_invitation: 'Invitations',
  notification_preference: 'Préférences de notification',
};

export const entityTypeLabel = (entityType: string): string => ENTITY_TYPE_LABELS[entityType] ?? entityType;

/** Fuseau dit a l'ecran : `UTC` devient une phrase, un autre fuseau reste tel que le serveur l'ecrit. */
export const describeTimezone = (timezone: string): string =>
  timezone === 'UTC' ? 'Les jours sont comptés en UTC.' : `Les jours sont comptés dans le fuseau ${timezone}.`;

export type SessionStateKind = 'active' | 'revoked' | 'expired';

export interface SessionState {
  readonly kind: SessionStateKind;
  readonly label: string;
}

/**
 * Etat d'une session. « Expiree » est deduite des deux champs que le serveur
 * rend (ni active, ni revoquee) : la session n'a plus de jeton de
 * rafraichissement valide.
 */
export const sessionState = (session: AdminUserSession): SessionState => {
  if (session.active) {
    return { kind: 'active', label: 'Active' };
  }

  return session.revokedAt === null
    ? { kind: 'expired', label: 'Expirée' }
    : { kind: 'revoked', label: 'Révoquée' };
};

/** Un appareil sans identifiant declare existe : il ne doit pas laisser une cellule blanche. */
export const deviceLabel = (session: AdminUserSession): string =>
  session.deviceHash === null || session.deviceHash === '' ? 'Appareil non identifié' : session.deviceHash;

/** Phrase de troncature, ou `null` quand la liste est complete. Le total est celui du serveur. */
export const describeSessionTruncation = (shown: number, total: number): string | null =>
  total > shown ? `Les ${String(shown)} sessions les plus récentes sur ${String(total)}.` : null;

/** Le serveur n'a rien recu sur la periode : aucune nature n'est rendue. */
export const activityIsEmpty = (activity: AdminUserActivity): boolean => activity.byEntityType.length === 0;
